// ============================================================
// Definición de las 12 actividades del Programa de Prevención de
// Riesgos Psicosociales (Ministerio del Trabajo del Ecuador)
//
// CITA REAL (fuente): las 12 actividades, la fórmula de cada indicador
// (numerador sobre denominador por 100) y los medios de verificación
// vienen del Anexo 1 de la Guía para la implementación del programa de
// prevención de riesgos psicosociales del MDT (actualización de
// noviembre de 2021). Los textos de `label`, `numeratorLabel`,
// `denominatorLabel` y `verificationNote` en este archivo son
// paráfrasis breves propias — no el texto oficial de la guía.
//
// DECISIÓN PROPIA (implementación, no de la fuente — pendiente de
// validar con Kevin):
//   - La agrupación en 'implementacion' / 'igualdad' / 'protocolo'
//     sigue los apartados 4.1, 4.2 y 4.3 de la guía, pero el nombre de
//     cada grupo (`ActivityGroup`) es decisión de este código.
//   - `PROGRAMA_EVIDENCE_KINDS` (la lista de tipos de evidencia) y qué
//     tipos exige cada actividad (`requiredEvidence`) son interpretación
//     propia de los medios de verificación que sugiere el Anexo 1 — la
//     guía no define una lista cerrada de "tipos de evidencia".
//   - Para la actividad 10 el Anexo 1 numera dos medios de verificación
//     (acta de entrega y evidencia de correo masivo) y aquí se exigen
//     AMBOS; pendiente confirmar con Kevin si basta con uno.
//
// NOTA: la pantalla de indicadores del SUT (manual de diciembre 2023)
// parece mostrar sus actividades con redacción y agrupación algo
// distintas (no verificado contra la pantalla actual) a las del Anexo 1.
// Este archivo sigue el Anexo 1 (12 actividades) — hay que contrastarlo
// contra la pantalla actual del SUT antes de que un cliente lo use para
// digitar ahí directamente.
// Existe además un trámite separado de "planificación de capacitación
// del programa de prevención de riesgos psicosociales" en el SUT,
// todavía no analizado.
// ============================================================

// Mismo orden que el CHECK de `kind` en
// supabase/migrations/20260920000300_psicosocial_programa_seguimiento.sql —
// ver test de deriva en psicosocial-programa.test.ts.
export const PROGRAMA_EVIDENCE_KINDS = [
  'material_informativo',
  'registro_asistencia',
  'acta_entrega',
  'correo_masivo',
  'plan_accion_firmado',
  'informe_seguimiento',
  'informe_resultados',
  'programa_firmado',
  'metodologia_cuestionario',
  'matriz_campanas',
  'otro',
] as const;

export type EvidenceKind = typeof PROGRAMA_EVIDENCE_KINDS[number];

export type DenominatorKind =
  | 'trabajadores'
  | 'medidas_plan'
  | 'medidas_implementadas'
  | 'actividades_programa'
  | 'campanas_planificadas';

export type ActivityGroup = 'implementacion' | 'igualdad' | 'protocolo';

export interface ProgramActivityDef {
  no: number;
  group: ActivityGroup;
  label: string;
  numeratorLabel: string;
  denominatorLabel: string;
  denominatorKind: DenominatorKind;
  requiredEvidence: EvidenceKind[];
  verificationNote: string;
}

export const PROGRAMA_ACTIVITIES_VERSION = 'anexo1-2021-11';

export const PROGRAMA_ACTIVITIES: ProgramActivityDef[] = [
  {
    no: 1,
    group: 'implementacion',
    label: 'Sensibilizar al personal sobre la prevención de riesgos psicosociales',
    numeratorLabel: 'Trabajadores que recibieron la sensibilización',
    denominatorLabel: 'Total de trabajadores y servidores',
    denominatorKind: 'trabajadores',
    requiredEvidence: ['material_informativo', 'registro_asistencia'],
    verificationNote: 'Material de sensibilización y registro de asistencia de quienes participaron.',
  },
  {
    no: 2,
    group: 'implementacion',
    label: 'Socializar el programa de prevención de riesgos psicosociales',
    numeratorLabel: 'Trabajadores que recibieron la socialización',
    denominatorLabel: 'Total de trabajadores y servidores',
    denominatorKind: 'trabajadores',
    requiredEvidence: ['material_informativo', 'registro_asistencia'],
    verificationNote: 'Material usado para socializar el programa y registro de asistencia.',
  },
  {
    no: 3,
    group: 'implementacion',
    label: 'Aplicar a todo el personal un instrumento de evaluación de riesgo psicosocial con validez y fiabilidad reconocidas',
    numeratorLabel: 'Trabajadores evaluados',
    denominatorLabel: 'Total de trabajadores y servidores',
    denominatorKind: 'trabajadores',
    requiredEvidence: ['metodologia_cuestionario', 'informe_resultados'],
    verificationNote: 'Ficha metodológica del instrumento usado e informe de resultados de la evaluación.',
  },
  {
    no: 4,
    group: 'implementacion',
    label: 'Socializar los resultados finales e identificar medidas de prevención',
    numeratorLabel: 'Trabajadores que recibieron la socialización de resultados',
    denominatorLabel: 'Total de trabajadores y servidores',
    denominatorKind: 'trabajadores',
    requiredEvidence: ['material_informativo', 'registro_asistencia'],
    verificationNote: 'Material de la socialización de resultados y registro de asistencia.',
  },
  {
    no: 5,
    group: 'implementacion',
    label: 'Estructurar y desarrollar un plan de acción con medidas preventivas y de intervención',
    numeratorLabel: 'Medidas implementadas',
    denominatorLabel: 'Medidas contenidas en el plan de acción',
    denominatorKind: 'medidas_plan',
    requiredEvidence: ['plan_accion_firmado'],
    verificationNote: 'Plan de acción firmado, con las medidas preventivas y de intervención definidas.',
  },
  {
    no: 6,
    group: 'implementacion',
    label: 'Dar seguimiento a las medidas implementadas para evidenciar su eficacia',
    numeratorLabel: 'Medidas con seguimiento',
    denominatorLabel: 'Medidas implementadas',
    denominatorKind: 'medidas_implementadas',
    requiredEvidence: ['informe_seguimiento'],
    verificationNote: 'Informe de seguimiento de las medidas ya implementadas.',
  },
  {
    no: 7,
    group: 'igualdad',
    label: 'Talleres de sensibilización en derechos laborales de grupos prioritarios, inclusión, igualdad, lenguaje positivo y no discriminación',
    numeratorLabel: 'Trabajadores que recibieron los talleres',
    denominatorLabel: 'Total de trabajadores y servidores',
    denominatorKind: 'trabajadores',
    requiredEvidence: ['material_informativo', 'registro_asistencia'],
    verificationNote: 'Material de los talleres y registro de asistencia de quienes participaron.',
  },
  {
    no: 8,
    group: 'igualdad',
    label: 'Programa de prevención de VIH-SIDA (sensibilización, prueba voluntaria y confidencial, canales de asesoría)',
    numeratorLabel: 'Actividades realizadas',
    denominatorLabel: 'Actividades detalladas en el programa',
    denominatorKind: 'actividades_programa',
    requiredEvidence: ['programa_firmado', 'material_informativo', 'registro_asistencia'],
    verificationNote: 'Programa firmado, material de sensibilización, y registro de asistencia de las actividades realizadas.',
  },
  {
    no: 9,
    group: 'protocolo',
    label: 'Socializar el protocolo de prevención y atención de casos de discriminación, acoso laboral y violencia contra la mujer',
    numeratorLabel: 'Trabajadores que recibieron la socialización',
    denominatorLabel: 'Total de trabajadores y servidores',
    denominatorKind: 'trabajadores',
    requiredEvidence: ['material_informativo', 'registro_asistencia'],
    verificationNote: 'Material de la socialización del protocolo y registro de asistencia.',
  },
  {
    no: 10,
    group: 'protocolo',
    label: 'Entregar al personal copia del protocolo, con evidencia de que conoce las conductas sujetas a sanción',
    numeratorLabel: 'Trabajadores que recibieron el protocolo',
    denominatorLabel: 'Total de trabajadores y servidores',
    denominatorKind: 'trabajadores',
    requiredEvidence: ['acta_entrega', 'correo_masivo'],
    verificationNote: 'Acta de entrega firmada y evidencia del envío masivo del protocolo.',
  },
  {
    no: 11,
    group: 'protocolo',
    label: 'Talleres de prevención de discriminación, acoso laboral y violencia contra la mujer, con derechos laborales, lenguaje positivo e inclusión',
    numeratorLabel: 'Trabajadores que recibieron los talleres',
    denominatorLabel: 'Total de trabajadores y servidores',
    denominatorKind: 'trabajadores',
    requiredEvidence: ['material_informativo', 'registro_asistencia'],
    verificationNote: 'Material de los talleres y registro de asistencia de quienes participaron.',
  },
  {
    no: 12,
    group: 'protocolo',
    label: 'Campañas comunicacionales permanentes sobre prevención de discriminación, acoso laboral y violencia contra la mujer',
    numeratorLabel: 'Campañas ejecutadas',
    denominatorLabel: 'Campañas planificadas',
    denominatorKind: 'campanas_planificadas',
    requiredEvidence: ['material_informativo', 'matriz_campanas'],
    verificationNote: 'Material de cada campaña y matriz que resume las campañas planificadas vs. ejecutadas.',
  },
];
